import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { Stack } from '@mui/material';
import { EMPLOYEE_CONTRACT_ASSIGMENT_TYPES } from 'domain/shared/employee';
import {
  canAssignPositionSlot,
  canCreatePositionFillRequest,
  canEditPositionSlot,
  canFreezePositionSlot,
  canRetirePositionSlot,
  canUnfreezePositionSlot,
} from 'domain/shared/positionSlot';
import { EMPTY_VALUE_INDICATOR } from 'framework/utils';
import type { FlatListTableProps, ListTableAction, ListTableColumn } from 'framework/ui/ui-builder';
import { POSITION_SLOT_LIFECYCLE_LABELS } from '../constants';
import type { PreviewPositionSlotListItem } from '../types';
import WorkerAndPfrCell from '../shared/fields/WorkerAndPfrCell';
import SecondaryCompetenciesTooltip from '../shared/fields/SecondaryCompetenciesTooltip';
import RenderCategory from '../shared/fields/RenderCategory';
import { SimpleLinkField } from 'framework/ViewTypes';

export type PositionSlotTableActions = {
  canCreate: boolean;
  canUpdate: boolean;
  canAssign: boolean;
  canFreeze: boolean;
  canUnfreeze: boolean;
  canRetire: boolean;
  canCreatePositionFillRequest: boolean;
  onCreate: () => void;
  onEdit: (positionSlot: PreviewPositionSlotListItem) => void;
  onAssign: (positionSlot: PreviewPositionSlotListItem) => void;
  onFreeze: (positionSlot: PreviewPositionSlotListItem) => void;
  onUnfreeze: (positionSlot: PreviewPositionSlotListItem) => void;
  onRetire: (positionSlot: PreviewPositionSlotListItem) => void;
  onCreatePositionFillRequest: (positionSlot: PreviewPositionSlotListItem) => void;
};

const getColumns = (getSingleEntityUrl: (id: string) => string): ListTableColumn<PreviewPositionSlotListItem>[] => [
  {
    id: 'specialization',
    label: 'Specialization',
    sortable: true,
    width: 180,
    render: positionSlot => (
      <Stack direction="row" alignItems="center" spacing={0.5} minWidth={0}>
        <SimpleLinkField to={getSingleEntityUrl(positionSlot.id)} value={positionSlot.specializationLabel} />
        <SecondaryCompetenciesTooltip positionSlot={positionSlot} />
      </Stack>
    ),
  },
  {
    id: 'technology',
    label: 'Technology',
    sortable: true,
    width: 150,
    render: positionSlot => positionSlot.technologyLabel || EMPTY_VALUE_INDICATOR,
  },
  {
    id: 'competencyLevel',
    label: 'Level',
    sortable: true,
    width: 110,
    render: positionSlot => positionSlot.competencyLevel || EMPTY_VALUE_INDICATOR,
  },
  {
    id: 'businessFunction',
    label: 'Business function',
    width: 180,
    render: positionSlot => positionSlot.businessFunctionLabel || EMPTY_VALUE_INDICATOR,
  },
  {
    id: 'workRateType',
    label: 'FTE',
    sortable: true,
    width: 110,
    render: positionSlot => EMPLOYEE_CONTRACT_ASSIGMENT_TYPES[positionSlot.workRateType],
  },
  {
    id: 'workerType',
    label: 'Category',
    sortable: true,
    width: 130,
    render: positionSlot => <RenderCategory positionSlot={positionSlot} />,
  },
  {
    id: 'offices',
    label: 'Location',
    sortable: true,
    width: 130,
    render: positionSlot => positionSlot.officesLabel || EMPTY_VALUE_INDICATOR,
  },
  {
    id: 'lifecycle',
    label: 'Status',
    sortable: true,
    width: 115,
    render: positionSlot => POSITION_SLOT_LIFECYCLE_LABELS[positionSlot.lifecycle] || positionSlot.lifecycle,
  },
  {
    id: 'worker',
    label: 'Worker / PFR',
    width: 220,
    render: positionSlot => <WorkerAndPfrCell positionSlot={positionSlot} />,
  },
];

const getActions = (config: PositionSlotTableActions): ListTableAction<PreviewPositionSlotListItem>[] => [
  {
    id: 'edit-position',
    label: 'Edit',
    icon: <EditOutlinedIcon />,
    variant: 'outlined',
    tooltip: 'Edit',
    isVisible: row => config.canUpdate && canEditPositionSlot(row),
    onClick: config.onEdit,
  },
  {
    id: 'assign-worker',
    label: 'Assign',
    icon: <PersonOutlineOutlinedIcon />,
    variant: 'outlined',
    tooltip: 'Assign worker',
    isVisible: row => config.canAssign && canAssignPositionSlot(row),
    onClick: config.onAssign,
  },
  {
    id: 'create-position-fill-request',
    label: 'Create position fill request',
    icon: <SendOutlinedIcon />,
    variant: 'outlined',
    tooltip: 'Create position fill request',
    isVisible: row => config.canCreatePositionFillRequest && canCreatePositionFillRequest(row),
    onClick: config.onCreatePositionFillRequest,
  },
  {
    id: 'freeze-position',
    label: 'Freeze',
    icon: <AcUnitIcon />,
    variant: 'outlined',
    tooltip: 'Freeze',
    isVisible: row => config.canFreeze && canFreezePositionSlot(row),
    onClick: config.onFreeze,
  },
  {
    id: 'unfreeze-position',
    label: 'Unfreeze',
    icon: <PlayArrowIcon />,
    variant: 'outlined',
    tooltip: 'Unfreeze',
    isVisible: row => config.canUnfreeze && canUnfreezePositionSlot(row),
    onClick: config.onUnfreeze,
  },
  {
    id: 'retire-position',
    label: 'Retire',
    icon: <ArchiveOutlinedIcon />,
    variant: 'outlined',
    tooltip: 'Retire',
    isVisible: row => config.canRetire && canRetirePositionSlot(row),
    onClick: config.onRetire,
  },
];

export const getPositionSlotsTableConfig = (
  rows: PreviewPositionSlotListItem[],
  getSingleEntityUrl: (id: string) => string,
  config: PositionSlotTableActions,
): FlatListTableProps<PreviewPositionSlotListItem> => {
  const actions = getActions(config);

  return {
    rows,
    columns: getColumns(getSingleEntityUrl),
    rowActions: { actions, maxActions: 3 },
    mainAction: config.canCreate
      ? {
          id: 'create-position',
          label: 'Create position',
          icon: <AddOutlinedIcon />,
          variant: 'contained',
          tooltip: 'Create position',
          onClick: config.onCreate,
        }
      : undefined,
    rowHeight: 45,
    emptyContent: 'No positions found.',
  };
};
